import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography, Box, Paper, TextField, Button, Avatar, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    IconButton, useMediaQuery, useTheme,
    InputAdornment, Dialog, DialogContent, DialogActions,
    Divider, Grid, Chip, CircularProgress,
    Menu, MenuItem, ListItemIcon, Tooltip
} from '@mui/material';
import { toastSuccess, confirmExclusao } from '../services/alerts';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BadgeIcon from '@mui/icons-material/Badge';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import CloseIcon from '@mui/icons-material/Close';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import AnexosPaciente from './AnexosPaciente';
import api from '../services/api';

const ESTADO_CIVIL_LABEL = {
    SOLTEIRO: 'Solteiro(a)',
    CASADO: 'Casado(a)',
    DIVORCIADO: 'Divorciado(a)',
    VIUVO: 'Viúvo(a)',
    UNIAO_ESTAVEL: 'União Estável',
};

const STATUS_CONFIG = {
    ATIVO:        { label: 'Ativo',        bg: '#dcfce7', color: '#16a34a' },
    EM_ANDAMENTO: { label: 'Em Andamento', bg: '#dbeafe', color: '#2563eb' },
    INATIVO:      { label: 'Inativo',      bg: '#f1f5f9', color: '#64748b' },
    ALTA:         { label: 'Alta',         bg: '#fef9c3', color: '#b45309' },
    EVADIDO:      { label: 'Evadido',      bg: '#fee2e2', color: '#dc2626' },
};

const InfoField = ({ label, value }) => (
    <Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.disabled', mb: 0.3 }}>
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={500} color={value ? 'text.primary' : 'text.disabled'} sx={{ wordBreak: 'break-word' }}>
            {value || '—'}
        </Typography>
    </Box>
);

const SectionCard = ({ icon, title, color = '#1ABB9C', children }) => (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, border: '1px solid #eef2f7', borderLeft: `4px solid ${color}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ color, display: 'flex' }}>{icon}</Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color }}>{title}</Typography>
        </Box>
        {children}
    </Paper>
);

export default function Pacientes() {
    const [pacientes, setPacientes] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Filtros e Busca
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({ nome: '', cpf: '', dataNascimento: '', statusTratamento: '', estadoCivil: '' });

    // Menu de ações por linha
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuPaciente, setMenuPaciente] = useState(null);

    const handleOpenMenu = (e, paciente) => { setMenuAnchor(e.currentTarget); setMenuPaciente(paciente); };
    const handleCloseMenu = () => { setMenuAnchor(null); setMenuPaciente(null); };

    // Modal de Visualização
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedPaciente, setSelectedPaciente] = useState(null);

    // Responsividade
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const debounceRef = useRef(null);
    const mountedRef = useRef(false);

    const carregarPacientes = async (pg, rpp, busca, filt) => {
        setLoading(true);
        try {
            const params = { page: pg, size: rpp };
            if (busca) params.busca = busca;
            if (filt.nome) params.nome = filt.nome;
            if (filt.cpf) params.cpf = filt.cpf;
            if (filt.dataNascimento) params.dataNascimento = filt.dataNascimento;
            if (filt.statusTratamento) params.statusTratamento = filt.statusTratamento;
            if (filt.estadoCivil) params.estadoCivil = filt.estadoCivil;

            const { data } = await api.get('/pacientes', { params });
            setPacientes(data.content ?? []);
            setTotalElements(data.totalElements ?? 0);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    };

    // Carga inicial
    useEffect(() => {
        mountedRef.current = true;
        carregarPacientes(0, rowsPerPage, '', { nome: '', cpf: '', dataNascimento: '', statusTratamento: '', estadoCivil: '' });
    }, []);

    // Mudança de filtros/busca — debounce 400ms + reset page
    useEffect(() => {
        if (!mountedRef.current) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(0);
            carregarPacientes(0, rowsPerPage, searchTerm, filters);
        }, 400);
        return () => clearTimeout(debounceRef.current);
    }, [searchTerm, filters]);

    // Mudança de página / tamanho — imediato
    useEffect(() => {
        if (!mountedRef.current) return;
        carregarPacientes(page, rowsPerPage, searchTerm, filters);
    }, [page, rowsPerPage]);

    const handleChangePage = (_, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleDelete = async (id) => {
        const ok = await confirmExclusao({ text: 'Deseja realmente excluir este paciente? Esta ação não pode ser desfeita.' });
        if (!ok) return;
        try {
            await api.delete(`/pacientes/${id}`);
            toastSuccess('Paciente excluído com sucesso');
            carregarPacientes(page, rowsPerPage, searchTerm, filters);
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    const handleLimparBuscaGeral = () => {
        setSearchTerm('');
    };

    const handleLimparFiltros = () => {
        setFilters({ nome: '', cpf: '', dataNascimento: '', statusTratamento: '', estadoCivil: '' });
    };

    const formatarData = (dataString) => {
        if (!dataString) return '';
        const [ano, mes, dia] = dataString.split('-');
        return `${dia}/${mes}/${ano}`;
    };

    const formatarCPF = (valor) => {
        if (!valor) return '';
        return valor.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    };

    const mascararCPF = (valor) => {
        if (!valor) return '';
        const limpo = valor.replace(/\D/g, '');
        if (limpo.length !== 11) return '***.***.***-**';
        return `***.${limpo.slice(3, 6)}.${limpo.slice(6, 9)}-**`;
    };

    const formatarTelefone = (valor) => {
        if (!valor) return '';
        if (valor.length === 11) {
            return valor.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        }
        return valor.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    };

    const handleOpenViewModal = (paciente) => {
        setSelectedPaciente(paciente);
        setViewModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setSelectedPaciente(null);
    };


    function renderModalContent() {
        if (!selectedPaciente) return null;
        const st = STATUS_CONFIG[selectedPaciente.statusTratamento]
            || { label: selectedPaciente.statusTratamento || 'Não definido', bg: '#f1f5f9', color: '#64748b' };
        return (
            <>
                {/* HEADER */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #1ABB9C 0%, #0e8a72 100%)',
                    px: { xs: 2.5, sm: 3.5 }, pt: 3, pb: 3,
                    display: 'flex', alignItems: 'flex-start', gap: 2,
                }}>
                    <Avatar sx={{
                        width: { xs: 52, sm: 64 }, height: { xs: 52, sm: 64 },
                        bgcolor: 'rgba(255,255,255,0.18)', color: 'white',
                        fontSize: { xs: '1.4rem', sm: '1.8rem' }, fontWeight: 700,
                        border: '3px solid rgba(255,255,255,0.35)', flexShrink: 0, mt: 0.5,
                    }}>
                        {selectedPaciente.nomeCompleto?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0, pr: 5 }}>
                        <Typography
                            variant={isMobile ? 'h6' : 'h5'} fontWeight={700} color="white"
                            sx={{ lineHeight: 1.2, mb: 1, wordBreak: 'break-word' }}
                        >
                            {selectedPaciente.nomeCompleto}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            <Chip label={st.label} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'white', fontWeight: 600, border: '1px solid rgba(255,255,255,0.35)', fontSize: '0.72rem' }} />
                            {selectedPaciente.estadoCivil && (
                                <Chip label={ESTADO_CIVIL_LABEL[selectedPaciente.estadoCivil] || selectedPaciente.estadoCivil} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.88)', fontSize: '0.72rem' }} />
                            )}
                            {selectedPaciente.profissao && (
                                <Chip label={selectedPaciente.profissao} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.88)', fontSize: '0.72rem' }} />
                            )}
                        </Box>
                    </Box>
                    <Tooltip title="Fechar">
                        <IconButton
                            onClick={handleCloseViewModal}
                            size="small"
                            aria-label="Fechar detalhes do paciente"
                            sx={{ color: 'rgba(255,255,255,0.8)', flexShrink: 0, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* BODY */}
                <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f4f6f8' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                        <SectionCard icon={<BadgeOutlinedIcon sx={{ fontSize: 19 }} />} title="Dados Pessoais">
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Nome Completo" value={selectedPaciente.nomeCompleto} /></Grid>
                                <Grid size={{ xs: 6, sm: 3 }}><InfoField label="CPF" value={formatarCPF(selectedPaciente.cpf)} /></Grid>
                                <Grid size={{ xs: 6, sm: 3 }}><InfoField label="RG" value={selectedPaciente.rg} /></Grid>
                                <Grid size={{ xs: 6, sm: 4 }}><InfoField label="Nascimento" value={formatarData(selectedPaciente.dataNascimento)} /></Grid>
                                <Grid size={{ xs: 6, sm: 4 }}><InfoField label="Estado Civil" value={ESTADO_CIVIL_LABEL[selectedPaciente.estadoCivil] || selectedPaciente.estadoCivil} /></Grid>
                                <Grid size={{ xs: 12, sm: 4 }}><InfoField label="Profissão" value={selectedPaciente.profissao} /></Grid>
                            </Grid>
                        </SectionCard>

                        <SectionCard icon={<PhoneOutlinedIcon sx={{ fontSize: 19 }} />} title="Contato" color="#6366f1">
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}><InfoField label="E-mail" value={selectedPaciente.email} /></Grid>
                                <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Telefone" value={formatarTelefone(selectedPaciente.telefone)} /></Grid>
                                <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Emergência — Nome" value={selectedPaciente.nomeContatoEmergencia} /></Grid>
                                <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Emergência — Telefone" value={formatarTelefone(selectedPaciente.telefoneContatoEmergencia)} /></Grid>
                            </Grid>
                        </SectionCard>

                        <SectionCard icon={<LocationOnOutlinedIcon sx={{ fontSize: 19 }} />} title="Endereço" color="#f59e0b">
                            {selectedPaciente.endereco ? (
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6, sm: 3 }}><InfoField label="CEP" value={selectedPaciente.endereco.cep} /></Grid>
                                    <Grid size={{ xs: 6, sm: 2 }}><InfoField label="Número" value={selectedPaciente.endereco.numero} /></Grid>
                                    <Grid size={{ xs: 12, sm: 7 }}><InfoField label="Logradouro" value={selectedPaciente.endereco.logradouro} /></Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}><InfoField label="Bairro" value={selectedPaciente.endereco.bairro} /></Grid>
                                    <Grid size={{ xs: 12, sm: 5 }}><InfoField label="Complemento" value={selectedPaciente.endereco.complemento} /></Grid>
                                    <Grid size={{ xs: 8, sm: 7 }}><InfoField label="Cidade" value={selectedPaciente.endereco.cidade?.nome} /></Grid>
                                    <Grid size={{ xs: 4, sm: 2 }}><InfoField label="UF" value={selectedPaciente.endereco.cidade?.estado?.sigla} /></Grid>
                                </Grid>
                            ) : (
                                <Typography variant="body2" color="text.disabled">Nenhum endereço cadastrado.</Typography>
                            )}
                        </SectionCard>

                        <SectionCard icon={<FavoriteOutlinedIcon sx={{ fontSize: 19 }} />} title="Tratamento" color="#ef4444">
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: st.bg }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: st.color, flexShrink: 0 }} />
                                <Typography variant="body2" fontWeight={600} sx={{ color: st.color }}>{st.label}</Typography>
                            </Box>
                        </SectionCard>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, bgcolor: 'white', borderTop: '1px solid #eee', gap: 1 }}>
                    <Button onClick={handleCloseViewModal} variant="outlined" color="inherit">Fechar</Button>
                    <Button
                        onClick={() => { handleCloseViewModal(); navigate(`/pacientes/${selectedPaciente.id_paciente}/ficha`); }}
                        variant="contained" startIcon={<EditIcon />}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}
                    >
                        Editar
                    </Button>
                </DialogActions>
            </>
        );
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Cabeçalho */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 2 : 0,
                mb: 3 
            }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="div" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Pacientes
                </Typography>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => navigate('/pacientes/novo')}
                    fullWidth={isMobile}
                    sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}
                >
                    Novo Paciente
                </Button>
            </Box>

            {/* Conteúdo Principal */}
            <Paper elevation={0} sx={{ 
                p: isMobile ? 1.5 : isTablet ? 2 : 3, 
                bgcolor: '#fff', 
                borderRadius: 2, 
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                overflowX: 'auto'
            }}>
                
                {/* Controles de Busca e Filtro */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                    
                    {/* Linha Principal: Busca e Botão de Filtros */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            placeholder="Busca rápida..."
                            variant="outlined"
                            size={isMobile ? 'small' : 'medium'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#999' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={handleLimparBuscaGeral} edge="end"
                                            aria-label="Limpar a busca">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ 
                                flex: '1 1 auto',
                                minWidth: isMobile ? '100%' : '250px',
                                '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fafafa' }
                            }}
                        />
                        
                        <Tooltip title="Filtros avançados">
                            <Box sx={{ position: 'relative' }}>
                                <IconButton
                                    onClick={() => setShowFilters(!showFilters)}
                                    aria-label={showFilters ? 'Fechar filtros avançados' : 'Abrir filtros avançados'}
                                    aria-expanded={showFilters}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: showFilters || filters.nome || filters.cpf || filters.dataNascimento || filters.statusTratamento ? 'primary.main' : '#e0e0e0',
                                        borderRadius: 2, p: 1.2,
                                        bgcolor: showFilters || filters.nome || filters.cpf || filters.dataNascimento || filters.statusTratamento ? 'rgba(25,118,210,0.08)' : 'transparent',
                                        color: showFilters || filters.nome || filters.cpf || filters.dataNascimento || filters.statusTratamento ? 'primary.main' : 'text.secondary',
                                    }}
                                >
                                    {showFilters ? <FilterListOffIcon /> : <FilterListIcon />}
                                </IconButton>
                                {(filters.nome || filters.cpf || filters.dataNascimento || filters.statusTratamento) && !showFilters && (
                                    <Box sx={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, bgcolor: 'error.main', borderRadius: '50%' }} />
                                )}
                            </Box>
                        </Tooltip>
                    </Box>

                    {/* Painel de Filtros Expansível */}
                    {showFilters && (
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Nome"
                                        value={filters.nome}
                                        onChange={(e) => setFilters({...filters, nome: e.target.value})}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="CPF"
                                        placeholder="Apenas números ou formatado"
                                        value={filters.cpf}
                                        onChange={(e) => setFilters({...filters, cpf: e.target.value})}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label="Data de Nascimento"
                                        InputLabelProps={{ shrink: true }}
                                        value={filters.dataNascimento}
                                        onChange={(e) => setFilters({...filters, dataNascimento: e.target.value})}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Status do Tratamento"
                                        value={filters.statusTratamento}
                                        onChange={(e) => setFilters({...filters, statusTratamento: e.target.value})}
                                        InputLabelProps={{ shrink: true, sx: { fontSize: '0.85rem' } }}
                                        SelectProps={{ displayEmpty: true }}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                            <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Estado Civil"
                                        value={filters.estadoCivil}
                                        onChange={(e) => setFilters({...filters, estadoCivil: e.target.value})}
                                        InputLabelProps={{ shrink: true, sx: { fontSize: '0.85rem' } }}
                                        SelectProps={{ displayEmpty: true }}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {Object.entries(ESTADO_CIVIL_LABEL).map(([key, label]) => (
                                            <MenuItem key={key} value={key}>{label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 12, md: 1 }}>
                                    {(filters.nome || filters.cpf || filters.dataNascimento || filters.statusTratamento || filters.estadoCivil) && (
                                        <IconButton color="error" onClick={handleLimparFiltros}
                                            title="Limpar Filtros" aria-label="Limpar todos os filtros">
                                            <ClearIcon />
                                        </IconButton>
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    )}
                </Box>

                {/* Informações sobre resultados */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                        {totalElements} paciente(s) encontrado(s)
                    </Typography>
                    {loading && <CircularProgress size={18} sx={{ color: '#1ABB9C' }} />}
                </Box>

                {/* Tabela */}
                <TableContainer>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold', maxWidth: { xs: 140, sm: 220, md: 300 } }}><strong>Nome</strong></TableCell>
                                <TableCell sx={{ fontWeight: 'bold', display: isMobile ? 'none' : 'table-cell', whiteSpace: 'nowrap' }}><strong>CPF</strong></TableCell>
                                <TableCell sx={{ fontWeight: 'bold', display: isTablet || isMobile ? 'none' : 'table-cell', whiteSpace: 'nowrap' }}><strong>Telefone</strong></TableCell>
                                <TableCell sx={{ fontWeight: 'bold', display: isTablet || isMobile ? 'none' : 'table-cell', whiteSpace: 'nowrap' }}><strong>Data Nasc.</strong></TableCell>
                                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pacientes.length > 0 ? pacientes.map((p) => (
                                <TableRow key={p.id_paciente} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                    <TableCell sx={{ maxWidth: { xs: 140, sm: 220, md: 300 }, overflow: 'hidden' }}>
                                        <Tooltip title={p.nomeCompleto || ''} placement="top-start">
                                            <Typography variant="body2" noWrap>{p.nomeCompleto}</Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell', whiteSpace: 'nowrap' }}>
                                        <Tooltip title={formatarCPF(p.cpf) || 'CPF não informado'} placement="top">
                                            <span>{mascararCPF(p.cpf)}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ display: isTablet || isMobile ? 'none' : 'table-cell', whiteSpace: 'nowrap' }}>{formatarTelefone(p.telefone)}</TableCell>
                                    <TableCell sx={{ display: isTablet || isMobile ? 'none' : 'table-cell', whiteSpace: 'nowrap' }}>{formatarData(p.dataNascimento)}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Ações" placement="left">
                                            <IconButton size="small" onClick={(e) => handleOpenMenu(e, p)}
                                                aria-label={`Ações de ${p.nomeCompleto}`}
                                                aria-haspopup="menu">
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        {loading ? (
                                            <CircularProgress sx={{ color: '#1ABB9C' }} />
                                        ) : (
                                            <>
                                                <AssignmentIndIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                                                <Typography variant="body1" sx={{ color: '#999' }}>
                                                    Nenhum paciente encontrado.
                                                </Typography>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                />
            </Paper>

            {/* Menu de Ações */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleCloseMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{ sx: { minWidth: 190, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
            >
                <Tooltip title="Abrir a ficha com atendimentos, prontuário, documentos e financeiro" placement="left">
                    <MenuItem onClick={() => { navigate(`/pacientes/${menuPaciente?.id_paciente}/ficha`); handleCloseMenu(); }}>
                        <ListItemIcon><FolderSharedIcon fontSize="small" color="primary" /></ListItemIcon>
                        Abrir ficha
                    </MenuItem>
                </Tooltip>
                <Tooltip title="Visualizar dados completos do paciente" placement="left">
                    <MenuItem onClick={() => { handleOpenViewModal(menuPaciente); handleCloseMenu(); }}>
                        <ListItemIcon><VisibilityIcon fontSize="small" color="info" /></ListItemIcon>
                        Ver Detalhes
                    </MenuItem>
                </Tooltip>
                <Tooltip title="Gerenciar arquivos anexados" placement="left">
                    <MenuItem onClick={() => { navigate(`/pacientes/${menuPaciente?.id_paciente}/anexos`); handleCloseMenu(); }}>
                        <ListItemIcon><AttachFileIcon fontSize="small" color="secondary" /></ListItemIcon>
                        Anexos
                    </MenuItem>
                </Tooltip>
                <Tooltip title="Carteirinhas de convênio do paciente" placement="left">
                    <MenuItem onClick={() => { navigate(`/pacientes/${menuPaciente?.id_paciente}/convenios`); handleCloseMenu(); }}>
                        <ListItemIcon><BadgeIcon fontSize="small" color="action" /></ListItemIcon>
                        Convênios
                    </MenuItem>
                </Tooltip>
                <Tooltip title="Editar dados do paciente" placement="left">
                    <MenuItem onClick={() => { navigate(`/pacientes/${menuPaciente?.id_paciente}`); handleCloseMenu(); }}>
                        <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                        Editar
                    </MenuItem>
                </Tooltip>
                <Divider />
                <Tooltip title="Excluir paciente permanentemente" placement="left">
                    <MenuItem
                        onClick={() => { handleDelete(menuPaciente?.id_paciente); handleCloseMenu(); }}
                        sx={{ color: 'error.main' }}
                    >
                        <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                        Excluir
                    </MenuItem>
                </Tooltip>
            </Menu>

            {/* Modal de Visualização de Detalhes do Paciente */}
            <Dialog
                open={viewModalOpen}
                onClose={handleCloseViewModal}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, overflow: 'hidden' } }}
            >
                {viewModalOpen && selectedPaciente && renderModalContent()}
            </Dialog>

        </Box>
    );
}